import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  EditableSlotGroup,
  EditableToken,
  useCollectionBinding,
  useContentToken,
  useEditMode,
  useProperty,
  usePropertyAddressUrl,
  useSlotGroup,
  useSocialLinks,
} from '@vibesites/runtime/react';
import { VibesitesImage, cn } from '@vibesites/ui-primitives';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFacebookF,
  faInstagram,
  faLinkedinIn,
  faTiktok,
  faXTwitter,
  faYelp,
  faYoutube,
} from '@fortawesome/free-brands-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Accessibility, PawPrint } from 'lucide-react';
import {
  btnPrimary,
  btnStone,
  clean,
  formatPhone,
  groupOfficeHours,
  hasValue,
  linkTarget,
  telHref,
  type OfficeHour,
} from '../utils/content';

const logoDef: SlotGroupDefinition = {
  label: 'Footer Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultField: 'property_mono_white_logo',
      suggestedFields: ['property_mono_white_logo', 'property_primary_logo'],
    },
  },
};

const applyLinkDef: SlotGroupDefinition = {
  label: 'Apply Button',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_apply_now_url', defaultValue: '/floor-plans', suggestedFields: ['property_apply_now_url', 'property_online_leasing_url'] },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Apply' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'apply_now_clicked' },
  },
};

const residentsLinkDef: SlotGroupDefinition = {
  label: 'Residents Button',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_resident_portal_url' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Residents' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'resident_portal_clicked' },
  },
};

const payRentLinkDef: SlotGroupDefinition = {
  label: 'Pay Rent Button',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_resident_portal_url' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Pay Your Rent' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'resident_portal_clicked' },
  },
};

const brochureLinkDef: SlotGroupDefinition = {
  label: 'Brochure Button',
  description: 'Link a PDF brochure. The button is hidden while the URL is empty.',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Brochure' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    address: { label: 'Address', type: 'text', defaultField: 'property_full_address' },
    url: { label: 'Directions URL (defaults to Google Maps)', type: 'url', defaultValue: '' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const companyDef: SlotGroupDefinition = {
  label: 'Management Company',
  fields: {
    image: {
      label: 'Company Logo',
      type: 'image',
      defaultField: 'property_company_mono_white_logo',
      suggestedFields: ['property_company_mono_white_logo', 'property_company_logo'],
    },
    url: { label: 'Company URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const privacyLinkDef: SlotGroupDefinition = {
  label: 'Privacy Policy Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const termsLinkDef: SlotGroupDefinition = {
  label: 'Terms Link',
  description: 'Hidden while the URL is empty.',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Terms and Conditions' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const fairHousingLinkDef: SlotGroupDefinition = {
  label: 'Fair Housing Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.hud.gov/program_offices/fair_housing_equal_opp' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Fair Housing Statement' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const accessibilityLinkDef: SlotGroupDefinition = {
  label: 'Accessibility Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility Statement' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef: SlotGroupDefinition = {
  label: 'Sitemap Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

function EqualHousingIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" className={className} fill="currentColor" aria-hidden="true">
      <path d="M16 3 2 13.5V16h3v13h22V16h3v-2.5L16 3Zm8 23H8V14.2l8-6 8 6V26Z" />
      <path d="M11 15h10v2.5H11zM11 19.5h10V22H11z" />
    </svg>
  );
}

const SOCIAL_ICONS: Array<[string, IconDefinition]> = [
  ['facebook', faFacebookF],
  ['instagram', faInstagram],
  ['twitter', faXTwitter],
  ['linkedin', faLinkedinIn],
  ['youtube', faYoutube],
  ['tiktok', faTiktok],
  ['yelp', faYelp],
];

const labelClass = 'font-heading text-[22px] font-normal uppercase leading-none tracking-wide text-[color:var(--vibe-color-text-on-secondary)]';
const legalLinkClass = 'font-body text-[13px] font-medium uppercase tracking-wide text-[color:var(--vibe-color-text-on-secondary)] no-underline opacity-90 transition-opacity duration-300 hover:opacity-100 hover:underline underline-offset-4';

export function Footer() {
  const editMode = useEditMode();
  const isEditing = !!editMode?.isEditMode;
  const property = useProperty() as unknown as Record<string, unknown>;
  const addressUrl = usePropertyAddressUrl();
  const socials = useSocialLinks() as Record<string, string | null | undefined>;

  const logo = useSlotGroup('footer.logo', logoDef);
  const apply = useSlotGroup('footer.apply_link', applyLinkDef);
  const residents = useSlotGroup('footer.residents_link', residentsLinkDef);
  const payRent = useSlotGroup('footer.pay_rent_link', payRentLinkDef);
  const brochure = useSlotGroup('footer.brochure_link', brochureLinkDef);
  const address = useSlotGroup('footer.address_link', addressLinkDef);
  const company = useSlotGroup('footer.company', companyDef);
  const privacy = useSlotGroup('footer.privacy_link', privacyLinkDef);
  const terms = useSlotGroup('footer.terms_link', termsLinkDef);
  const fairHousing = useSlotGroup('footer.fair_housing_link', fairHousingLinkDef);
  const accessibility = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const sitemap = useSlotGroup('footer.sitemap_link', sitemapLinkDef);

  const phone = useContentToken('footer.phone');
  const email = useContentToken('footer.email');
  const copyright = useContentToken('footer.copyright');
  const locationLabel = useContentToken('footer.location_label');
  const contactLabel = useContentToken('footer.contact_label');
  const hoursLabel = useContentToken('footer.hours_label');
  const managedLabel = useContentToken('footer.managed_label');

  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', { collection: 'office_hours' });
  const hours = groupOfficeHours(officeHours || []);

  const propertyName = String(property?.property_name ?? '');
  const phoneValue = hasValue(phone) ? phone : String(property?.property_phone ?? '');
  const logoUrl = hasValue(logo.image) ? logo.image : '';

  const companyName = clean(property?.property_company_name);
  const companyLogo = hasValue(company.image) ? company.image : (hasValue(property?.property_company_logo) ? String(property.property_company_logo) : '');
  const companyOnChip = !hasValue(company.image) && !!companyLogo;

  const socialEntries = SOCIAL_ICONS
    .filter(([key]) => hasValue(socials?.[key]))
    .map(([key, icon]) => ({ key, url: String(socials[key]), icon }));

  const petFriendly = String(property?.property_ispetfriendly ?? '').toLowerCase() === 'true';
  const year = new Date().getFullYear();
  const copyrightText = hasValue(copyright)
    ? copyright.replace(/^©\s*/, '')
    : `${year} All Rights Reserved. ${propertyName}`;

  // Links with an empty URL are hidden on the live site and dimmed in the editor.
  const renderCta = (data: Record<string, string | null>, fallback: string, cls: string) => {
    const empty = !hasValue(data.url);
    return (
      <a
        href={data.url || '#'}
        target={linkTarget(data.url, data._target)}
        rel="noopener noreferrer"
        data-vibesite-event={data.vibesite_event || undefined}
        className={cn(cls, 'w-full !min-h-[40px] !py-2.5 !text-[16px]', empty && (isEditing ? 'opacity-40' : 'hidden'))}
      >
        {data.label || fallback}
      </a>
    );
  };

  const renderLegal = (data: Record<string, string | null>) => {
    const empty = !hasValue(data.url);
    return (
      <a
        href={data.url || '#'}
        target={linkTarget(data.url, data._target)}
        className={cn(legalLinkClass, empty && (isEditing ? 'opacity-40' : 'hidden'))}
      >
        {data.label}
      </a>
    );
  };

  return (
    <footer className="bg-secondary text-[color:var(--vibe-color-text-on-secondary)]">
      <div className="mx-auto max-w-[1280px] px-6 pb-10 pt-14 md:px-10 lg:pt-16">
        <div className="js-stagger grid grid-cols-1 gap-10 text-center sm:grid-cols-2 sm:text-left lg:grid-cols-[1.15fr_1fr_1.1fr_1fr_1.2fr] lg:gap-8">
          <div className="flex flex-col items-center gap-5 sm:items-start">
            <EditableSlotGroup groupPath="footer.logo" definition={logoDef}>
              <a href="/" aria-label={`${propertyName || 'Home'} home`} className="block">
                {logoUrl ? (
                  <VibesitesImage
                    url={logoUrl}
                    alt={propertyName}
                    sizes="220px"
                    className="h-auto max-h-16 w-auto max-w-[220px] object-contain"
                  />
                ) : (
                  <span className="block max-w-[260px] font-heading text-[24px] font-semibold uppercase leading-tight tracking-[0.16em]">{propertyName}</span>
                )}
              </a>
            </EditableSlotGroup>
            {socialEntries.length > 0 && (
              <ul className="flex items-center gap-4">
                {socialEntries.map((s) => (
                  <li key={s.key}>
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.key}
                      className="inline-flex h-9 w-9 items-center justify-center text-[color:var(--vibe-color-text-on-secondary)] transition-[color,transform] duration-300 ease-out hover:-translate-y-1 hover:scale-110 hover:text-primary"
                    >
                      <FontAwesomeIcon icon={s.icon} className="h-5 w-5" />
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mx-auto flex w-full max-w-[220px] flex-col gap-3 sm:mx-0">
            <EditableSlotGroup groupPath="footer.apply_link" definition={applyLinkDef}>
              {renderCta(apply, 'Apply', btnPrimary)}
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="footer.residents_link" definition={residentsLinkDef}>
              {renderCta(residents, 'Residents', btnStone)}
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="footer.pay_rent_link" definition={payRentLinkDef}>
              {renderCta(payRent, 'Pay Your Rent', btnPrimary)}
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="footer.brochure_link" definition={brochureLinkDef}>
              {renderCta(brochure, 'Brochure', btnStone)}
            </EditableSlotGroup>
          </div>

          <div className="flex flex-col gap-4">
            <EditableToken path="footer.location_label" as="h2" className={labelClass}>
              {locationLabel || 'Location'}
            </EditableToken>
            <EditableSlotGroup groupPath="footer.address_link" definition={addressLinkDef}>
              <a
                href={address.url || addressUrl || '#'}
                target={address._target || '_blank'}
                rel="noopener noreferrer"
                className="block font-body text-[17px] leading-relaxed text-[color:var(--vibe-color-text-on-secondary)] no-underline hover:underline underline-offset-4"
              >
                {address.address || String(property?.property_full_address ?? '')}
              </a>
            </EditableSlotGroup>
          </div>

          <div className="flex flex-col gap-4">
            <EditableToken path="footer.contact_label" as="h2" className={labelClass}>
              {contactLabel || 'Contact'}
            </EditableToken>
            {hasValue(phoneValue) && (
              <a href={telHref(phoneValue)} className="font-body text-[20px] font-medium text-[color:var(--vibe-color-text-on-secondary)] no-underline hover:underline underline-offset-4">
                <EditableToken path="footer.phone" as="span">
                  {formatPhone(phoneValue)}
                </EditableToken>
              </a>
            )}
            {hasValue(email) && (
              <a href={`mailto:${email}`} className="break-all font-body text-[16px] text-[color:var(--vibe-color-text-on-secondary)] opacity-90 no-underline hover:underline underline-offset-4">
                <EditableToken path="footer.email" as="span">
                  {email}
                </EditableToken>
              </a>
            )}
          </div>

          <div className="flex flex-col gap-4 sm:col-span-2 lg:col-span-1">
            <EditableToken path="footer.hours_label" as="h2" className={labelClass}>
              {hoursLabel || 'Office Hours'}
            </EditableToken>
            <EditableCollectionBinding bindingPath="footer.office_hours" label="Office Hours">
              {hours.length > 0 ? (
                <dl className="mx-auto grid w-full max-w-[320px] grid-cols-[auto_1fr] gap-x-6 gap-y-3 text-left font-body text-[17px] sm:mx-0">
                  {hours.map((h) => (
                    <div key={h.label} className="contents">
                      <dt className="whitespace-nowrap">{h.label}</dt>
                      <dd className="whitespace-nowrap">{h.hours}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="font-body text-[17px] text-[color:var(--vibe-color-text-on-secondary)] opacity-90">Call or email to set up a visit.</p>
              )}
            </EditableCollectionBinding>
          </div>
        </div>

        <div className="js-reveal mt-14 flex items-center justify-center gap-10 text-[color:var(--vibe-color-text-on-secondary)]" aria-label="Housing statements">
          <EqualHousingIcon className="h-7 w-7" />
          {petFriendly && <PawPrint className="h-7 w-7" aria-label="Pet friendly" />}
          <Accessibility className="h-7 w-7" aria-label="Accessible" />
        </div>

        <nav aria-label="Legal" className="mt-6">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
            <EditableSlotGroup groupPath="footer.privacy_link" definition={privacyLinkDef}>
              {renderLegal(privacy)}
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="footer.terms_link" definition={termsLinkDef}>
              {renderLegal(terms)}
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="footer.fair_housing_link" definition={fairHousingLinkDef}>
              {renderLegal(fairHousing)}
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="footer.accessibility_link" definition={accessibilityLinkDef}>
              {renderLegal(accessibility)}
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
              {renderLegal(sitemap)}
            </EditableSlotGroup>
          </div>
        </nav>

        <p className="mt-6 text-center font-body text-[13px] uppercase tracking-wide text-[color:var(--vibe-color-text-on-secondary)] opacity-90">
          ©{' '}
          <EditableToken path="footer.copyright" as="span">
            {copyrightText}
          </EditableToken>
        </p>

        {(companyLogo || hasValue(companyName)) && (
          <div className="mt-8 flex flex-col items-center justify-center gap-3 border-t border-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_18%,transparent)] pt-8 sm:flex-row">
            <EditableToken path="footer.managed_label" as="span" className="font-body text-[12px] uppercase tracking-[0.18em] text-[color:var(--vibe-color-text-on-secondary)] opacity-80">
              {managedLabel || 'Professionally managed by'}
            </EditableToken>
            <EditableSlotGroup groupPath="footer.company" definition={companyDef}>
              <a
                href={hasValue(company.url) ? company.url : '#'}
                target={company._target || '_blank'}
                rel="noopener noreferrer"
                className="inline-flex items-center text-[color:var(--vibe-color-text-on-secondary)] no-underline"
              >
                {companyLogo ? (
                  <VibesitesImage
                    url={companyLogo}
                    alt={companyName || 'Management company'}
                    sizes="160px"
                    className={cn('h-8 w-auto max-w-[160px] object-contain', companyOnChip && 'rounded-sm bg-background px-2 py-1 box-content')}
                  />
                ) : (
                  <span className="font-heading text-lg font-bold">{companyName}</span>
                )}
              </a>
            </EditableSlotGroup>
          </div>
        )}
      </div>
    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
    'footer.email': { label: 'Email', type: 'email', defaultField: 'property_email' },
    'footer.copyright': {
      label: 'Copyright',
      type: 'text',
      defaultField: 'property_copyright',
      defaultValue: 'All Rights Reserved.',
    },
    'footer.location_label': { label: 'Location Heading', type: 'text', defaultValue: 'Location' },
    'footer.contact_label': { label: 'Contact Heading', type: 'text', defaultValue: 'Contact' },
    'footer.hours_label': { label: 'Office Hours Heading', type: 'text', defaultValue: 'Office Hours' },
    'footer.managed_label': { label: 'Managed By Label', type: 'text', defaultValue: 'Professionally managed by' },
  },
  slotGroups: {
    'footer.logo': logoDef,
    'footer.apply_link': applyLinkDef,
    'footer.residents_link': residentsLinkDef,
    'footer.pay_rent_link': payRentLinkDef,
    'footer.brochure_link': brochureLinkDef,
    'footer.address_link': addressLinkDef,
    'footer.company': companyDef,
    'footer.privacy_link': privacyLinkDef,
    'footer.terms_link': termsLinkDef,
    'footer.fair_housing_link': fairHousingLinkDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
  },
  collectionBindings: {
    'footer.office_hours': { label: 'Office Hours', collection: 'office_hours' },
  },
};

(Footer as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Footer;
